import type { IBrand, INote, IPerfumer, ICountry, IPrice } from '../../../pocketbase/PocketbaseMetadataService'

interface ImageCellProps {
  imgUrl: string | null
}

export function ImageCell({ imgUrl }: ImageCellProps) {
  return imgUrl ? (
    <img
      src={imgUrl}
      alt="Fragrance"
      style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
    />
  ) : (
    <div style={{ width: 60, height: 60, backgroundColor: '#f0f0f0', borderRadius: 4 }} />
  )
}

interface NameCellProps {
  name: string
}

export function NameCell({ name }: NameCellProps) {
  return (
    <div style={{
      maxWidth: 200,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical'
    }}>
      {name}
    </div>
  )
}

interface BrandCellProps {
  brand?: IBrand
  brandText?: string
}

export function BrandCell({ brand, brandText }: BrandCellProps) {
  const displayValue = brand?.name || brandText
  const isCustom = !!brandText

  return (
    <div style={{
      maxWidth: 150,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      fontWeight: isCustom ? 'bold' : 'normal'
    }}>
      {displayValue || 'Unknown'}
    </div>
  )
}

interface DescriptionCellProps {
  description: string
}

export function DescriptionCell({ description }: DescriptionCellProps) {
  return (
    <div style={{
      maxWidth: 300,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical'
    }}>
      {description || 'No description'}
    </div>
  )
}

interface CountryCellProps {
  country?: ICountry
  countryText?: string
}

export function CountryCell({ country, countryText }: CountryCellProps) {
  const displayValue = country?.name || countryText
  const isCustom = !!countryText

  return (
    <div style={{
      maxWidth: 150,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      fontWeight: isCustom ? 'bold' : 'normal'
    }}>
      {displayValue || 'Unknown'}
    </div>
  )
}

interface YearCellProps {
  year: number | null
}

export function YearCell({ year }: YearCellProps) {
  return <span>{year || 'Unknown'}</span>
}

interface PriceCellProps {
  price?: IPrice
}

export function PriceCell({ price }: PriceCellProps) {
  return (
    <div style={{
      maxWidth: 100,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical'
    }}>
      {price?.name || 'Unknown'}
    </div>
  )
}

interface PerfumerCellProps {
  perfumers: IPerfumer[]
  perfumersText?: string[]
}

export function PerfumerCell({ perfumers, perfumersText = [] }: PerfumerCellProps) {
  const dbPerfumers = perfumers.map(p => p.name)
  const customPerfumers = perfumersText

  return (
    <div style={{
      maxWidth: 150,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical'
    }}>
      {dbPerfumers.length === 0 && customPerfumers.length === 0 ? 'Unknown' : (
        <>
          {dbPerfumers.join(', ')}
          {dbPerfumers.length > 0 && customPerfumers.length > 0 && ', '}
          <span style={{ fontWeight: 'bold' }}>{customPerfumers.join(', ')}</span>
        </>
      )}
    </div>
  )
}

interface NotesCellProps {
  notes: INote[]
  notesText?: string[]
}

export function NotesCell({ notes, notesText = [] }: NotesCellProps) {
  const dbNotes = notes.map(n => n.name)
  const customNotes = notesText

  return (
    <div style={{
      maxWidth: 200,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical'
    }}>
      {dbNotes.length === 0 && customNotes.length === 0 ? 'None' : (
        <>
          {dbNotes.join(', ')}
          {dbNotes.length > 0 && customNotes.length > 0 && ', '}
          <span style={{ fontWeight: 'bold' }}>{customNotes.join(', ')}</span>
        </>
      )}
    </div>
  )
}

interface DateCellProps {
  date: string
}

export function DateCell({ date }: DateCellProps) {
  const dateObj = new Date(date)
  return <span>{dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString()}</span>
}
